import { useEffect, useState, useMemo, useCallback } from "react";
import { useFlows, Flow } from "../../../../../stores/flowsStore";

export type FlowsFilter = "all" | "active" | "inactive" | "draft";

export const useFlowsManagerView = () => {
  const {
    setSubView,
    flows,
    flowsLoading,
    fetchFlows,
    updateFlow,
    deleteFlow,
    duplicateFlow,
    setEditingFlow,
    error,
    clearError,
  } = useFlows();

  const [filter, setFilter] = useState<FlowsFilter>("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchFlows();
  }, [fetchFlows]);

  const handleFilterChange = useCallback(
    (_: React.SyntheticEvent, newValue: FlowsFilter) => {
      setFilter(newValue);
    },
    []
  );

  const filteredFlows = useMemo(
    () =>
      flows.filter((flow) => {
        if (filter === "all") return true;
        return flow.status === filter;
      }),
    [flows, filter]
  );

  const handleCreateNew = useCallback(() => {
    setEditingFlow(null);
    setSubView("automation-flow");
  }, [setEditingFlow, setSubView]);

  const handleEdit = useCallback(
    (flow: Flow) => {
      setEditingFlow(flow);
      setSubView("automation-flow");
    },
    [setEditingFlow, setSubView]
  );

  const handleDeleteClick = useCallback((flowId: string) => {
    setFlowToDelete(flowId);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (flowToDelete) {
      setDeleteLoading(true);
      await deleteFlow(flowToDelete);
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setFlowToDelete(null);
    }
  }, [flowToDelete, deleteFlow]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setFlowToDelete(null);
  }, []);

  const handleToggleStatus = useCallback(
    async (flow: Flow) => {
      const newStatus = flow.status === "active" ? "inactive" : "active";
      await updateFlow(flow.identifier, { status: newStatus });
    },
    [updateFlow]
  );

  const handleDuplicate = useCallback(
    async (flow: Flow) => {
      await duplicateFlow(flow.identifier);
    },
    [duplicateFlow]
  );

  const counts = useMemo(
    () => ({
      all: flows.length,
      active: flows.filter((f) => f.status === "active").length,
      inactive: flows.filter((f) => f.status === "inactive").length,
      draft: flows.filter((f) => f.status === "draft").length,
    }),
    [flows]
  );

  const handleBackToMenu = useCallback(() => {
    setSubView("menu");
  }, [setSubView]);

  return {
    filter,
    filteredFlows,
    flowsLoading,
    error,
    clearError,
    counts,
    deleteDialogOpen,
    deleteLoading,
    handleFilterChange,
    handleCreateNew,
    handleEdit,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleToggleStatus,
    handleDuplicate,
    handleBackToMenu,
  };
};
